import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';

import * as signalR from '@microsoft/signalr';

import { Table } from '@/common';
import { Button, Label, LABEL_TYPE, Spinner, StatusOverview } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { PAGE_SIZE } from '@/constants';
import { AuthContext } from '@/contexts';
import { PRIVATE_PAGE_ROUTES, TributeVideoExportStatus } from '@/enums';
import { getTributeVideoExportColumns, webSocketApiUrl } from '@/helpers';
import { useMobileScreen } from '@/hooks';
import {
  useGetAllTributeVideoExports,
  useGetNumberOverviewQuery,
  useGetStatusOverviewQuery,
  useGetWeeklyExportsQuery
} from '@/services';
import { SelectOptionProps, TableData, TributeVideoExport } from '@/types';

import { DashboardFilters } from './DashboardFilters';
import { WeeklyExportsChart } from './WeeklyExportsChart';

export const Dashboard = () => {
  const [t] = useTranslation('common');
  const navigate = useNavigate();
  const isMobileView = useMobileScreen();
  const { currentUserData: currentUser } = useContext(AuthContext);
  const [search, setSearch] = useState<string>();
  const [appliedFiltersCounter, setAppliedFiltersCounter] = useState<number>(0);
  const [filters, setFilters] = useState<{
    status: SingleValue<SelectOptionProps>;
  }>({
    status: null
  });
  const [isFilterModalVisible, setIsFilterModalVisible] = useState<boolean>(false);

  const filterButtonRef = useRef<HTMLDivElement | null>(null);
  const [filterButtonPosition, setFilterButtonPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0
  });

  const {
    data: statusOverviewData,
    isLoading: isLoadingStatusOverview,
    refetch: statusOverviewRefetch
  } = useGetStatusOverviewQuery(currentUser?.id as number);
  const { data: numberOverviewData, isLoading: isLoadingNumberOverview } =
    useGetNumberOverviewQuery(currentUser?.id as number);
  const { data: weeklyExportsData, isLoading: isLoadingWeeklyExports } = useGetWeeklyExportsQuery(
    currentUser?.id as number
  );

  const handleTributeVideoClick = (tributeVideoId: number, eventId: number) => {
    const tributeVideoUrl = PRIVATE_PAGE_ROUTES.CREATE_TRIBUTE_VIDEO.replace(
      ':eventId',
      eventId.toString()
    ).replace(':tributeVideoId', tributeVideoId.toString());
    navigate(tributeVideoUrl);
  };

  const handleEventClick = (eventId: number) => {
    const eventUrl = PRIVATE_PAGE_ROUTES.SINGLE_EVENT.replace(':eventId', eventId.toString());
    navigate(eventUrl);
  };

  const handleOrganizationClick = (organizationId: number) => {
    const organizationUrl = PRIVATE_PAGE_ROUTES.SINGLE_ORGANIZATION.replace(
      ':organizationId',
      organizationId.toString()
    );
    navigate(organizationUrl);
  };

  const handleFilterModalVisibility = () => {
    if (filterButtonRef.current) {
      const rect = filterButtonRef.current.getBoundingClientRect();
      const offset = 8;
      const modalWidth = 420;

      setFilterButtonPosition({
        top: rect.bottom + window.scrollY + offset,
        left: rect.right + window.scrollX - modalWidth
      });
    }

    setIsFilterModalVisible((prev) => !prev);
  };

  const {
    isLoading,
    data: tributeVideoExports,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch
  } = useGetAllTributeVideoExports(
    PAGE_SIZE,
    search,
    filters.status?.value as TributeVideoExportStatus,
    currentUser?.id as number
  );

  useEffect(() => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${webSocketApiUrl()}/notificationHub`, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets
      })
      .build();

    connection.on('tributeVideoProgressChanged', () => {
      refetch();
      statusOverviewRefetch();
    });

    const startPromise = connection.start().catch(err => console.log('err', err));

    return () => {
      connection.off('tributeVideoProgressChanged');
      startPromise.finally(() => {
        connection.stop().catch(err => console.log('err', err));
      });
    };
  }, [refetch, statusOverviewRefetch]);

  const searchCallback = (query: string) => {
    setSearch(query);
  };

  const resetFilters = () => {
    setIsFilterModalVisible(false);
    setFilters({ status: null });
    setAppliedFiltersCounter(0);
  };

  const applyFilters = (values: Record<string, SelectOptionProps>) => {
    setIsFilterModalVisible(false);
    setFilters({
      status: values.filterByStatus
    });
    setAppliedFiltersCounter(values.filterByStatus ? 1 : 0);
  };

  const tableData: TableData[] =
    tributeVideoExports?.pages.flatMap((page) =>
      page.data?.items.map((item: TributeVideoExport, index: number) => ({
        ...item,
        key: `${item.id}-${index}`,
        id: item.id
      }))
    ) || [];

  if (isLoading || isLoadingStatusOverview || isLoadingNumberOverview || isLoadingWeeklyExports) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <Spinner />
      </div>
    );
  }

  return (
    <div className='flex h-screen w-full flex-col overflow-hidden'>
      <div className={`${isMobileView ? 'mx-3 my-5' : 'mx-8 mb-10 mt-7 grid grid-cols-4 gap-6'}`}>
        {!isMobileView && (
          <div className='col-span-1 rounded-lg border border-gray-300 bg-white p-3 shadow-md'>
            <div className='mb-4 flex items-center justify-between'>
              <Label type={LABEL_TYPE.H3} text={t('numberOverview')} />
              <Button variant={ButtonVariant.SENARY}>{t('last24h')}</Button>
            </div>
            {numberOverviewData && (
              <div className='space-y-2'>
                {Object.entries(numberOverviewData).map(([key, value]) => (
                  <div key={key} className='flex justify-between'>
                    <Label type={LABEL_TYPE.PARAGRAPH1} text={t(`${key.toLowerCase()}`)} />
                    <Label type={LABEL_TYPE.H4} text={value as number} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {!isMobileView && (
          <div className='col-span-1 rounded-lg border border-gray-300 bg-white p-3 shadow-md'>
            <Label type={LABEL_TYPE.H3} text={t('weeklyExports')} />
            {weeklyExportsData && (
              <div className='mt-3 w-full'>
                <WeeklyExportsChart weeklyExportsData={weeklyExportsData} />
              </div>
            )}
          </div>
        )}
        <div
          className={`${!isMobileView && 'col-span-2'} rounded-lg border border-gray-300 bg-white p-3 shadow-md`}>
          <div className='mb-4 flex items-center justify-between'>
            <Label type={LABEL_TYPE.H3} text={t('statusOverview')} />
            <Button variant={ButtonVariant.SENARY}>{t('last24h')}</Button>
          </div>
          {statusOverviewData && (
            <StatusOverview
              isMobileView={isMobileView ?? false}
              statusOverviewData={statusOverviewData}
            />
          )}
        </div>
      </div>
      <Table
        data={tableData}
        columns={getTributeVideoExportColumns(
          handleTributeVideoClick,
          handleEventClick,
          handleOrganizationClick
        )}
        tableTitle={t('dashboard')}
        permissions={{}}
        search={searchCallback}
        handleOpenFilterModal={handleFilterModalVisibility}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        numberOfAppliedFilters={appliedFiltersCounter}
        hiddenColumnsMobile={['type', 'organizationName', 'createdAt', 'updatedAt', 'createdBy']}
        isDashboardPage
        filterButtonRef={filterButtonRef}
      />
      {isFilterModalVisible && (
        <DashboardFilters
          apply={applyFilters}
          reset={resetFilters}
          onClose={() => setIsFilterModalVisible(!isFilterModalVisible)}
          filterByStatus={filters.status}
          buttonPosition={filterButtonPosition}
        />
      )}
    </div>
  );
};
